import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { closestCorners, DndContext } from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import {
  LayeredRulingContext,
  RuleState,
} from '@/features/editor/widgets/custom-widget/loading-section/layered-ruling/context';
import {
  LoadingStrategyOption,
  RecommendationType,
} from '@/webapi/use-widget-catalog-api';
import { Slot } from '@/features/editor/widgets/custom-widget/loading-section/shared/slot';
import { defaultCondition } from '@/features/editor/widgets/custom-widget/loading-section/util/defaultCondition';
import { hashSlot } from '@/features/editor/widgets/custom-widget/loading-section/shared/util/hashSlot';
import { SlotState } from '@/features/editor/widgets/custom-widget/loading-section/shared/models';
import { VSpace } from '@/components/spacing';
import { CustomWidgetContext } from '@/features/editor/widgets/custom-widget/shared/context';
import {
  SavedFiltersManual,
  SavedStrategies,
} from '@/features/editor/widgets/custom-widget/loading-section/strategy-per-slot/saved-strategy/saved-filters';
import { useSlotsStrategyApi } from '@/features/editor/widgets/custom-widget/loading-section/strategy-per-slot/saved-strategy/use-slots-strategy-api';

type Props = { ruleId: number };

export function StrategyPerSlot({ ruleId }: Props) {
  const ctx = useContext(LayeredRulingContext);
  const widgetContextProps = useContext(CustomWidgetContext);
  const rule = ctx.rule(ruleId);
  const numOfSlots = useWatchSlotsSize(rule, ruleId);

  const { getListSlotsStrategies, saveSlotsStrategy, deleteSlotsStrategy } =
    useSlotsStrategyApi();
  const [slotsStrategies, setSlotsStrategy] = useState<SavedStrategies>([]);

  useEffect(() => {
    const fetchSavedStrategies = async () => {
      const strategies = await getListSlotsStrategies();
      if (strategies && strategies.length > 0) {
        setSlotsStrategy(strategies);
      }
    };

    fetchSavedStrategies();
  }, []);

  const onMoreFilters = (s: SlotState) => () => {
    ctx.changeState((draft) => {
      draft.activeCondSlot = s.num;
      draft.showCondBuilder = true;
    });
  };
  const onStrategySelect =
    (s: SlotState) => (o: LoadingStrategyOption) => () => {
      ctx.changeSlot(ruleId, s.num, (s) => {
        delete s.manualProduct;
        s.strategy = o.value.type;
        s.filter = ctx.isGlobalFilterApplied
          ? ctx.globalFilter
          : defaultCondition(
              s.strategy,
              widgetContextProps?.currentSchema?.appId,
            );
        s.hash = hashSlot(s);
      });
    };
  const onDuplicateSelect = (s: SlotState) => (o: number) => () => {
    const duplicatedSlot = rule?.strategyPerSlot?.slots[o];
    if (duplicatedSlot) {
      ctx.changeSlot(ruleId, s.num, (s) => {
        s.manualProduct = duplicatedSlot.manualProduct;
        s.strategy = duplicatedSlot.strategy;
        s.filter = duplicatedSlot.filter;
        s.hash = hashSlot(s);
      });
    }
  };
  const onDelete = (s: SlotState) => () => {
    ctx.changeSlot(ruleId, s.num, (s) => {
      s.manualProduct = undefined;
      s.strategy = undefined;
      if (!ctx.isGlobalFilterApplied) s.filter = undefined;
      s.hash = undefined;
    });
  };
  const onProductSelect = (s: SlotState) => () => {
    ctx.setProductSearchSlotOpen(s.num);
    ctx.changeSlot(ruleId, s.num, (s) => {
      s.strategy = RecommendationType.MANUAL;
      s.filter = ctx.isGlobalFilterApplied
        ? ctx.globalFilter
        : defaultCondition(
            RecommendationType.MANUAL,
            widgetContextProps?.currentSchema?.appId,
          );
      s.hash = hashSlot(s);
    });
  };

  if (rule.strategy !== RecommendationType.MANUAL) {
    return null;
  }
  const changeNumForSlot = (num: number, newNum: number) => {
    ctx.changeState((draft) => {
      draft.rules.forEach((r) => {
        if (r.id === ruleId) {
          r.strategyPerSlot.slots.forEach((slot) => {
            if (slot.num === num) {
              slot.num = newNum;
            } else if (slot.num === newNum) {
              slot.num = num;
            }
          });
          r.strategyPerSlot.slots.sort((a, b) => a.num - b.num);
        }
      });
    });
  };
  const setStrategy = (slots: Array<SlotState>) => {
    const defaultFilter = defaultCondition(RecommendationType.MANUAL);
    ctx.setIsGlobalFilterApplied(false);
    ctx.setGlobalFilter(defaultFilter);
    let newGlobalFilterIsApplied = false;
    ctx.changeState((draft) => {
      draft.rules.forEach((r) => {
        if (r.id === ruleId) {
          r.strategyPerSlot.slots.forEach((slot) => {
            delete slot.manualProduct;
            delete slot.strategy;
            delete slot.filter;
            delete slot.isGlobalFilter;
            delete slot.hash;

            const newSlot = slots.find((s) => s.num === slot.num);
            if (newSlot) {
              Object.assign(slot, newSlot);
              if (!newSlot.filter) {
                slot.filter = defaultFilter;
              }
              if (
                !newGlobalFilterIsApplied &&
                newSlot.isGlobalFilter &&
                newSlot.filter
              ) {
                ctx.setIsGlobalFilterApplied(true);
                ctx.setGlobalFilter(newSlot.filter);
                newGlobalFilterIsApplied = true;
              }
            }
          });
          r.strategyPerSlot.slots.sort((a, b) => a.num - b.num);
        }
      });
    });
  };
  const saveStrategy = async (name: string) => {
    const strategy = {
      name,
      slots: { slots: rule.strategyPerSlot.slots },
    };

    await saveSlotsStrategy(strategy);
    const strategies = await getListSlotsStrategies();
    setSlotsStrategy(strategies);
  };

  const updateStrategy = async (id: string) => {
    const strategy = {
      id,
      slots: { slots: rule.strategyPerSlot.slots },
    };

    await saveSlotsStrategy(strategy);
    const strategies = await getListSlotsStrategies();
    setSlotsStrategy(strategies);
  };

  const deleteStrategy = async (id: string) => {
    await deleteSlotsStrategy(id);
    const strategies = await getListSlotsStrategies();
    setSlotsStrategy(strategies);
  };
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id === over.id) return;
    changeNumForSlot(active.id, over.id);
  };

  const sortableItems = rule.strategyPerSlot.slots.map((s) => ({
    ...s,
    id: s.num,
  }));

  return (
    <DndContext collisionDetection={closestCorners} onDragEnd={handleDragEnd}>
      <SortableContext items={sortableItems}>
        <Container>
          <Header>
            <Span>Product/Strategy per slot ({numOfSlots} slots)</Span>
            <SavedFiltersManual
              openGlobal={() => ctx.setIsGlobalFilterOpen(true)}
              onSaveStrategy={saveStrategy}
              onUpdateStrategy={updateStrategy}
              onDeleteStrategy={deleteStrategy}
              setStrategy={(s) => setStrategy(s.slots)}
              strategies={slotsStrategies}
              isGlobalFilterApplied={ctx.isGlobalFilterApplied}
            />
          </Header>
          <VSpace />
          <Grid>
            {rule.strategyPerSlot.slots.map((s, index) => (
              <Slot
                key={`${s.hash || ``}${s.num || ``}`}
                slot={s}
                onMoreFilters={onMoreFilters(s)}
                onStrategySelect={onStrategySelect(s)}
                onDelete={onDelete(s)}
                onProductSelect={onProductSelect(s)}
                onDuplicateSelect={onDuplicateSelect(s)}
                index={index}
                slots={rule.strategyPerSlot.slots}
              />
            ))}
          </Grid>
        </Container>
      </SortableContext>
    </DndContext>
  );
}

const Span = styled.div`
  text-transform: uppercase;
  text-align: start;
  font-weight: 600;
  font-size: 1.4rem;
  color: #959ab5;
`;
const Header = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin: 1rem 0 2rem 0;
`;
const Container = styled.div`
  margin-top: 1rem;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-gap: 2rem;
  width: 100%;
`;

function useWatchSlotsSize(rule: RuleState, ruleId: number) {
  const ctx = useContext(LayeredRulingContext);
  const numOfSlots = ctx.getManualSlots().length;
  useEffect(() => {
    if (rule?.strategyPerSlot?.slots?.length !== numOfSlots) {
      ctx.change(ruleId, (r) => {
        if (!r.strategyPerSlot) {
          r.strategyPerSlot = { slots: [] };
        }

        r.strategyPerSlot.slots = ctx
          .getManualSlots()
          .map(
            (empty) =>
              r.strategyPerSlot.slots.find(
                (existing) => existing.num === empty.num,
              ) || empty,
          );
      });
    }
  }, [numOfSlots]);
  return numOfSlots;
}
